import React from 'react';

import { observer } from 'mobx-react';
import { reverse } from 'named-urls';
import { useNavigate, useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { InputAdornment } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';

import Grid from 'components/Grid';
import type { FilterType } from 'components/Grid/types';
import { VatixError } from 'components/Error/types';
import SearchIcon from 'components/Icons/Search';
import { StyledTextField } from 'components/Input/styles';
import AssignedToPicker from 'containers/Tasks/components/AssignedToPicker';
import DueDatePicker from 'containers/Tasks/components/DueDatePicker';
import StatusPicker from 'containers/Tasks/components/StatusPicker';
import { FiltersContainer, ResetButton, Spacer, VerticalDivider } from 'containers/Tasks/styles';
import { UserRole } from 'core/constants';
import routes from 'core/routes';
import SessionUser from 'stores/Session/SessionUser';
import { RISK_ACTIONS_URL, RiskActionRow, RiskActionStatus } from 'utils/api/modules/risks';
import { readerPeriod } from 'utils/formatters/timezone';
import { useStore } from 'utils/hooks/store';

import { riskActionColumns } from './columns';
import ControlActionDrawer from './ControlActionDrawer/ControlActionDrawer';
import RiskActionTypePicker from './RiskActionTypePicker';
import { RISK_ACTION_STATUS_OPTIONS } from './statuses';
import { StyledActionDrawer } from './styles';
import { useRiskActionsFilters } from './useRiskActionsFilters';

interface RiskActionsTabProps {
  onError: (error: VatixError) => void;
}

const RiskActionsTabContent: React.FunctionComponent<RiskActionsTabProps & { user: SessionUser }> = ({
  onError,
  user,
}) => {
  const navigate = useNavigate();
  const { actionId } = useParams<{ actionId?: string }>();
  const gridApiRef = useGridApiRef();
  const refreshRef = React.useRef<(() => void) | null>(null);

  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState('');
  const debouncedSetQuery = React.useMemo(() => debounce(setDebouncedQuery, 300), []);
  React.useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

  const temporaryFilters = React.useMemo<FilterType[]>(
    () => (debouncedQuery ? [{ field: 'query', operator: 'is', value: debouncedQuery }] : []),
    [debouncedQuery]
  );

  const {
    filters,
    initialFilters,
    hasActiveFilters,
    handleTypeChange,
    handleAssignedToChange,
    handleDueDateChange,
    handleStatusChange,
    handleResetFilters,
  } = useRiskActionsFilters({ gridApiRef, refreshRef, user, defaultDueDate: readerPeriod('month') });

  const closeDrawer = (): void => {
    navigate(routes.dashboard.tasks.risks.toString());
  };

  return (
    <>
      <FiltersContainer>
        <RiskActionTypePicker value={filters.type} onChange={handleTypeChange} />
        {user.role !== UserRole.User ? (
          <AssignedToPicker
            value={filters.assignedTo}
            onChange={handleAssignedToChange}
            licenses="risks"
            testId="risk-actions-assigned-filter"
          />
        ) : null}
        <DueDatePicker
          value={filters.dueDate}
          onChange={handleDueDateChange}
          clearable={false}
          testId="risk-actions-due-date-filter"
        />
        <StatusPicker<RiskActionStatus>
          value={filters.status}
          onChange={handleStatusChange}
          options={RISK_ACTION_STATUS_OPTIONS}
          testId="risk-actions-status-filter"
        />
        <VerticalDivider />
        {hasActiveFilters && <ResetButton onClick={handleResetFilters}>Reset filters</ResetButton>}
        <Spacer />
        <StyledTextField
          placeholder="Search"
          size="small"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            debouncedSetQuery(e.target.value);
          }}
          inputProps={{ 'data-testid': 'risk-actions-search', 'aria-label': 'Search' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </FiltersContainer>
      <div data-testid="risk-actions-grid">
        <Grid
          basicColumns={riskActionColumns}
          entity="action"
          onError={onError}
          dataURL={RISK_ACTIONS_URL}
          onRowClick={({ row }) =>
            navigate(reverse(routes.dashboard.tasks.riskAction, { actionId: (row as RiskActionRow).uuid }))
          }
          onRefreshCallback={(callback) => {
            refreshRef.current = callback;
          }}
          sortBy={{ field: 'dueDate', sort: 'asc' }}
          customNoRowsText="No risk actions match the selected filters"
          disableColumnMenu
          disableColumnSelector
          disableColumnFilter
          disableSavePreferences
          gridApiRef={gridApiRef}
          getRowId={(row) => row.uuid}
          showHeader={false}
          temporaryFilters={temporaryFilters}
          filters={initialFilters}
        />
      </div>
      <StyledActionDrawer anchor="right" open={Boolean(actionId)} onClose={closeDrawer}>
        {actionId && (
          <ControlActionDrawer actionId={actionId} onClose={closeDrawer} onUpdate={() => refreshRef.current?.()} />
        )}
      </StyledActionDrawer>
    </>
  );
};

const ObservedRiskActionsTabContent = observer(RiskActionsTabContent);

const RiskActionsTab: React.FunctionComponent<RiskActionsTabProps> = (props) => {
  const { session } = useStore();

  if (!session.user) return null;

  return <ObservedRiskActionsTabContent {...props} user={session.user} />;
};

export default observer(RiskActionsTab);
